import {ActivityType} from './activity';
import {RaceRankingMethod} from './event';
import {TeamMemberRole} from './member';
import {LinkageService} from './tatta-user';

export interface OfficialTeam {
  id: number; // チームのid partitionKey
  name: string; // チーム名 gsi partitionKey
  activityType: ActivityType; // gsi sortKey
  type: TeamType;
  category: TeamCategory;
  status: TeamStatus;
  aclTeam: TeamAcl; // チームの参加条件。アプリ上での露出に大きく関わる設定でオフィシャルチーム管理者が自由に変更できるとビジネス的に問題があるので、TATTA admin側でのみ変更可能
  aclPostFeed: TeamPostAcl; // 掲示板投稿権限
  aclPostTeamHistory: TeamPostAcl; // 活動実績投稿権限。基本的には0で、UI上は表示しない。 オフィシャルチームのみ設定を変更できる。
  aclCreateIntraEvent: TeamPostAcl; // チーム内イベント作成権限
  aclPostTeamCourse: TeamPostAcl; // ホームコース登録権限
  aclInviteMember: TeamPostAcl; // メンバー招待権限
  area?: string; // 活動エリア
  updatedAt: number; // 更新日時
  nameUpdatedAt?: number; // チーム名更新日時。登録時にはundefined
  description: string; // チームの紹介文
  webLink?: string; // チームのWebページリンク
  twitterLink?: string; // チームのtwitterリンク
  facebookLink?: string; // チームのfacebookリンク
  instagramLink?: string; // チームのinstagramリンク
  youtubeLink?: string; // チームのyoutubeリンク
  iconUrl: string; // チームのアイコン画像URL
  imageUrl: string; // チームのカバー画像URL
  membership: number; // メンバー数
  capacity: number; // 定員。0の場合は無制限
  eventsTotal: number; // 活動イベント数
  monthlyTotalDistance: number; // 月間距離
  since?: number; // 発足年
  paidAccount: TeamPaidAccount;
  paidPlan: TeamPaidPlan;
  paidPlanHistories: TeamPaidPlan[];
  connectedRuntesRaces: ConnectedRace[];
  connectedMoshicomEvents: ConnectedRace[];
  requiredServices: LinkageService[]; //　参加時に連携が必要なサービス（複数ある場合はor条件）
  eventRequiredServices: LinkageService[]; //　チームイベント参加時に連携が必要なサービス（作成時に選択可能）
}

export interface TeamPaidAccount {
  name: string;  // アカウント名
  email: string;  // アカウントメールアドレス
  denyNotifyFeed?: boolean; // 掲示板への投稿通知を受け取らないか否か
  denyNotifyImportant?: boolean; // 重要なお知らせを受け取らないか否か
  userId?: number; // TATTAユーザーIDと紐付けることが可能。紐付けるとチームオーナーではなく、個人として掲示板投稿やイベント作成ができるようになる
}

export interface TeamRelatedAccount extends TeamPaidAccount {
  role: TeamMemberRole.admin | TeamMemberRole.ambassador;
}

export interface TeamPaidPlan {
  additionalCapacity: number; // Team#capacityにこの値を契約時に追加する。一度増えた定員は戻さない（契約切れの場合はsuspendとするため）
  additionalEvents: RaceRankingMethod[]; // 開催可能なイベント種別(suspend状態の場合は無効)
  startedAt: number;
  expiredAt: number; // 期限が切れそうになったら営業全体MLに通知。Xヶ月以上経過する場合は自動でsuspend
  price: number; // アプリ側でのソート順に利用する
}

export enum TeamType {
  general = 0, // ActivityTypeごとに参加可能な一般的なチーム
  official = 1, // ActivityTypeによる制限を受けず、いくつでも参加可能なスポンサー運営のチーム。チーム対抗イベントには参加できない
  certificated = 2, // 一般チームの中で公益性を伴う活動実績があり、審査に通った場合のみ有効とするもの
  officialPrivate = 3, // ActivityTypeによる制限を受けず、いくつでも参加可能なメンバー限定公開のチーム。健康経営企業等で利用。チーム対抗イベントには参加できない
  officialRaceCommunity = 4, // ActivityTypeによる制限を受けず、いくつでも参加可能な大会主催者運営のチーム。チーム対抗イベントには参加できない
}

export const getTeamTypeName = (teamType: TeamType): string => {
  switch (teamType) {
    // TeamAdminでは TeamType.general が入ってくることは無いはずだが定義は用意
    case TeamType.general:
      return '一般チーム';
    case TeamType.official:
      return 'オフィシャルチーム';
    case TeamType.certificated:
      return '認証チーム';
    case TeamType.officialPrivate:
      return 'オフィシャルチーム（メンバー限定公開）';
    case TeamType.officialRaceCommunity:
      return '大会公式コミュニティ';
    default:
      return '-';
  }
}

export enum TeamCategory {
  circle = 0,
  student = 1,
  service = 2,
  company = 3,
  association = 4,
  municipality = 5,
  other = 6,
}

export const getTeamCategoryName = (category: TeamCategory): string => {
  switch (category) {
    case TeamCategory.circle:
      return '走友会・サークル';
    case TeamCategory.student:
      return '学生団体';
    case TeamCategory.service:
      return 'サービス・ショップ';
    case TeamCategory.company:
      return '企業';
    case TeamCategory.association:
      return '協会';
    case TeamCategory.municipality:
      return '行政・自治体';
    case TeamCategory.other:
      return 'その他';
  }
}

export interface categoryCheck {
  value: number;
  name: string;
}

export const getTeamCategoryCheckList = (): categoryCheck[] => {
  let keys = Object.keys(TeamCategory);
  keys = keys.slice(keys.length / 2);

  return keys.map((key: string, idx: number) => {
    return {
      value: idx,
      name: getTeamCategoryName(idx),
    };
  });
}

export enum TeamStatus {
  active = 0,
  suspended = 1, // 休止中。チーム参加・招待、イベント開催、掲示板・活動履歴への投稿ができない、集計が行われない
  disbanded = 2, // 解散済み
  draft = 3,  // 下書き（非公開）。チーム登録直後の状態でアプリ上に表示されない
}

export const TeamStatusName = [
  'アクティブ',
  '休止中',
  '解散済み',
  '下書き（非公開）',
];

export enum TeamAcl {
  everyone = 0,
  invitedOnly = 1,
}

export const getTeamAclName = (acl: TeamAcl): string => {
  switch (acl) {
    case TeamAcl.everyone:
      return '誰でも参加可能';
    case TeamAcl.invitedOnly:
      return '個別に招待';
  }
}

export enum TeamPostAcl {
  member = 0,
  adminOnly = 1,
}

export const getTeamPostAclName = (acl: TeamPostAcl, teamType: TeamType): string => {
  switch (acl) {
    case TeamPostAcl.member:
      return '全メンバー';
    case TeamPostAcl.adminOnly:
      return teamType === TeamType.officialPrivate ? '管理者のみ' : '管理者/アンバサダーのみ';
  }
}

export interface ConnectedRace {
  title: string;
  url: string;
  distanceName?: string;
}

export interface EventConnectedRace extends ConnectedRace {
  actionOnConnect: EventActionOnConnect;
}

export enum EventActionOnConnect {
  InviteAndEntry, // 招待と同時にイベントへ自動参加した状態とする
  Invite, // 招待のみ行う
}

export interface TeamMoshicomUserConnection {
  team_id: number; // part key
  moshicom_user_id: number; // sort key RUNNET顧客番号
  name: string;
  url: string;
}
